// @ts-strict-ignore
import { useCallback } from 'react'
import {
  AmplitudeEventAction,
  AmplitudeEventCategory,
  AmplitudeEventType,
} from 'utils/amplitude/amplitudeEvents'
import track from 'utils/tracker'
import {
  cancelEventMap,
  errorEventMap,
  formEventMap,
  pageEventMap,
  successEventMap,
} from '../constants/eventMap'

export interface RewardsTrackingContext {
  currency?: string
  amount?: string
  walletId?: string
  walletName?: string
  portfolioId?: string
  productId?: string
  tab?: string
  action?: string
}

export interface RewardsTrackingConfig {
  category?: string
  action?: (typeof AmplitudeEventAction)[keyof typeof AmplitudeEventAction]
  context?: RewardsTrackingContext
}

export type RewardsTrackingOperation =
  | 'allocate'
  | 'withdraw'
  | 'customerWithdraw'
  | 'removeAllocation'
  | 'createPortfolio'
export type RewardsTrackingFormType = 'allocate' | 'withdraw' | 'removeAllocation'
export type RewardsTrackingPage = 'home' | 'details'

export const useRewardsTracking = () => {
  const trackPageView = useCallback(
    (page: RewardsTrackingPage, context?: RewardsTrackingContext) => {
      track.Amp.track(pageEventMap[page], {
        category: AmplitudeEventCategory.MERCHANT_PORTAL,
        action: AmplitudeEventAction.VIEW,
        ...context,
      })
    },
    []
  )

  const trackFormInitiate = useCallback(
    (formType: RewardsTrackingFormType, context?: RewardsTrackingContext) => {
      track.Amp.track(formEventMap[formType], {
        category: AmplitudeEventCategory.MERCHANT_PORTAL,
        action: AmplitudeEventAction.VIEW,
        ...context,
      })
    },
    []
  )

  const trackFormCancel = useCallback(
    (formType: RewardsTrackingFormType, context?: RewardsTrackingContext) => {
      track.Amp.track(cancelEventMap[formType], {
        category: AmplitudeEventCategory.MERCHANT_PORTAL,
        action: AmplitudeEventAction.CLICK,
        ...context,
      })
    },
    []
  )

  const trackSuccess = useCallback(
    (operation: RewardsTrackingOperation, context?: RewardsTrackingContext) => {
      track.Amp.track(successEventMap[operation], {
        category: AmplitudeEventCategory.MERCHANT_PORTAL,
        ...context,
      })
    },
    []
  )

  const trackError = useCallback(
    (operation: RewardsTrackingOperation, error: any, context?: RewardsTrackingContext) => {
      track.Amp.track(errorEventMap[operation], {
        category: AmplitudeEventCategory.MERCHANT_PORTAL,
        action: AmplitudeEventAction.ERROR,
        error,
        ...context,
      })
    },
    []
  )

  const trackClick = useCallback(
    (
      eventName: AmplitudeEventType,
      context?: RewardsTrackingContext,
      config?: RewardsTrackingConfig
    ) => {
      track.Amp.track(eventName, {
        category: config?.category || AmplitudeEventCategory.MERCHANT_PORTAL,
        action: config?.action || AmplitudeEventAction.CLICK,
        ...config?.context,
        ...context,
      })
    },
    []
  )

  return {
    trackPageView,
    trackFormInitiate,
    trackFormCancel,
    trackSuccess,
    trackError,
    trackClick,
  }
}

export default useRewardsTracking
